import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Play,
  Pause,
  Trash2,
  Download,
  Zap,
  Activity,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  MinusCircle,
  Loader2,
  Eye,
  Wifi,
  WifiOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";

const AD_FORMATS = ["Banner", "Video", "Native", "Audio"];
const TRAFFIC_TYPES = ["Mobile_app", "Mobile_web", "Desktop", "CTV"];
const COUNTRIES = ["US", "GB", "DE", "FR", "CA", "AU", "JP", "BR", "IN", "MX"];

export default function BidSimulationPage() {
  const { api } = useAuth();
  const [ssps, setSsps] = useState([]);
  const [dsps, setDsps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [connected, setConnected] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const wsRef = useRef(null);

  const [config, setConfig] = useState({
    ssp_id: "",
    dsp_ids: [],
    request_count: 5,
    ad_format: "Banner",
    traffic_type: "Mobile_app",
    country: "US",
  });

  useEffect(() => {
    fetchData();
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const [sspsRes, dspsRes, logsRes] = await Promise.all([
        api.get("/ssps"),
        api.get("/dsps"),
        api.get("/simulation/logs?limit=100"),
      ]);
      setSsps(sspsRes.data);
      setDsps(dspsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const connectWebSocket = () => {
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/ws/logs`);

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const log = JSON.parse(event.data);
      setLogs((prev) => [log, ...prev].slice(0, 500));
    };

    ws.onclose = () => {
      setConnected(false);
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };

    wsRef.current = ws;
  };

  const runSimulation = async () => {
    if (!config.ssp_id) {
      toast.error("Please select an SSP");
      return;
    }

    if (liveMode && dsps.length === 0) {
      toast.error("Live mode requires at least one DSP endpoint configured");
      return;
    }

    setRunning(true);
    try {
      const endpoint = liveMode ? "/simulation/run-live" : "/simulation/run";
      const response = await api.post(endpoint, config);
      const mode = response.data.mode === "live" ? "Live" : "Simulated";
      toast.success(`${mode} simulation complete: ${response.data.winning_bids} winning bids`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Simulation failed");
    } finally {
      setRunning(false);
    }
  };

  const clearLogs = async () => {
    try {
      await api.delete("/simulation/logs");
      setLogs([]);
      toast.success("Logs cleared");
    } catch (error) {
      toast.error("Failed to clear logs");
    }
  };

  const exportLogs = async (format) => {
    try {
      const response = await api.get(`/simulation/logs/export?format=${format}`, {
        responseType: format === "csv" ? "blob" : "json",
      });

      if (format === "csv") {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bid_logs.csv";
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bid_logs.json";
        a.click();
      }
      toast.success(`Logs exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export logs");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      won: { color: "bg-emerald-500/10 text-emerald-500", icon: CheckCircle },
      lost: { color: "bg-red-500/10 text-red-500", icon: XCircle },
      no_bid: { color: "bg-zinc-500/10 text-zinc-500", icon: MinusCircle },
      bid: { color: "bg-blue-500/10 text-blue-500", icon: Activity },
    };
    const variant = variants[status] || variants.no_bid;
    const Icon = variant.icon;

    return (
      <Badge className={`${variant.color} gap-1`} variant="outline">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const toggleDsp = (dspId) => {
    setConfig((prev) => ({
      ...prev,
      dsp_ids: prev.dsp_ids.includes(dspId)
        ? prev.dsp_ids.filter((id) => id !== dspId)
        : [...prev.dsp_ids, dspId],
    }));
  };

  // Calculate stats with proper null handling
  const stats = {
    total: logs.length,
    won: logs.filter((l) => l.status === "won").length,
    noBid: logs.filter((l) => l.status === "no_bid").length,
    avgLatency: logs.length
      ? Math.round(logs.reduce((sum, l) => sum + (l.latency_ms || 0), 0) / logs.length) || 0
      : 0,
    avgBidPrice: logs.filter((l) => l.bid_price).length
      ? (
          logs.filter((l) => l.bid_price).reduce((sum, l) => sum + (l.bid_price || 0), 0) /
          logs.filter((l) => l.bid_price).length
        ).toFixed(4)
      : "0.0000",
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bid-simulation-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-8 w-8 text-accent" />
            Bid Simulation
          </h1>
          <p className="text-muted-foreground">
            Test OpenRTB bid requests and responses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={connected ? "text-emerald-500" : "text-red-500"}
          >
            {connected ? "Live Connected" : "Disconnected"}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Simulation Config</CardTitle>
            <CardDescription>Configure bid request parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>SSP Endpoint *</Label>
              <Select
                value={config.ssp_id}
                onValueChange={(v) => setConfig((prev) => ({ ...prev, ssp_id: v }))}
              >
                <SelectTrigger data-testid="ssp-select">
                  <SelectValue placeholder="Select SSP" />
                </SelectTrigger>
                <SelectContent>
                  {ssps.map((ssp) => (
                    <SelectItem key={ssp.id} value={ssp.id}>
                      {ssp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Target DSPs (optional)</Label>
              <ScrollArea className="h-32 border rounded-md p-2">
                {dsps.map((dsp) => (
                  <div key={dsp.id} className="flex items-center space-x-2 py-1">
                    <Checkbox
                      id={`dsp-${dsp.id}`}
                      checked={config.dsp_ids.includes(dsp.id)}
                      onCheckedChange={() => toggleDsp(dsp.id)}
                    />
                    <Label htmlFor={`dsp-${dsp.id}`} className="text-sm cursor-pointer">
                      {dsp.name}
                    </Label>
                  </div>
                ))}
                {dsps.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No DSPs available. Mock DSP will be used.
                  </p>
                )}
              </ScrollArea>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ad Format</Label>
                <Select
                  value={config.ad_format}
                  onValueChange={(v) => setConfig((prev) => ({ ...prev, ad_format: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AD_FORMATS.map((f) => (
                      <SelectItem key={f} value={f}>
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Traffic Type</Label>
                <Select
                  value={config.traffic_type}
                  onValueChange={(v) => setConfig((prev) => ({ ...prev, traffic_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAFFIC_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t.replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country</Label>
                <Select
                  value={config.country}
                  onValueChange={(v) => setConfig((prev) => ({ ...prev, country: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Request Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={config.request_count}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      request_count: parseInt(e.target.value) || 1,
                    }))
                  }
                  data-testid="request-count-input"
                />
              </div>
            </div>

            {/* Live Mode Toggle */}
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                {liveMode ? (
                  <Wifi className="h-5 w-5 text-emerald-500" />
                ) : (
                  <WifiOff className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <Label className="text-sm font-medium">Live Mode</Label>
                  <p className="text-xs text-muted-foreground">
                    {liveMode ? "Call real DSP endpoints" : "Use simulated responses"}
                  </p>
                </div>
              </div>
              <Switch
                checked={liveMode}
                onCheckedChange={setLiveMode}
                data-testid="live-mode-switch"
              />
            </div>

            <Button
              className="w-full"
              onClick={runSimulation}
              disabled={running || !config.ssp_id}
              data-testid="run-simulation-btn"
            >
              {running ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  {liveMode ? "Run Live Simulation" : "Run Simulation"}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Stats and Logs */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Total Requests</p>
              <p className="text-2xl font-bold font-mono">{stats.total}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Winning Bids</p>
              <p className="text-2xl font-bold font-mono text-emerald-500">{stats.won}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">No Bids</p>
              <p className="text-2xl font-bold font-mono text-zinc-500">{stats.noBid}</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Avg Latency</p>
              <p className="text-2xl font-bold font-mono">{stats.avgLatency}ms</p>
            </Card>
            <Card className="p-4">
              <p className="text-sm text-muted-foreground">Avg Bid Price</p>
              <p className="text-2xl font-bold font-mono">${stats.avgBidPrice}</p>
            </Card>
          </div>

          {/* Logs Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-accent" />
                  Bid Logs
                </CardTitle>
                <CardDescription>Real-time bid request/response logs</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => exportLogs("csv")}>
                  <Download className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportLogs("json")}>
                  <Download className="mr-2 h-4 w-4" />
                  JSON
                </Button>
                <Button variant="outline" size="sm" onClick={clearLogs}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>SSP</TableHead>
                      <TableHead>DSP</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead className="text-right">Floor</TableHead>
                      <TableHead className="text-right">Bid</TableHead>
                      <TableHead className="text-right">Latency</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </TableCell>
                        <TableCell className="text-sm">{log.ssp_name}</TableCell>
                        <TableCell className="text-sm">{log.dsp_name || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {log.ad_format || "Banner"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          ${(log.bid_floor ?? log.request?.imp?.[0]?.bidfloor ?? 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {log.bid_price ? `$${log.bid_price.toFixed(4)}` : "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {log.latency_ms ?? 0}ms
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          No bid logs yet. Run a simulation to see results.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Bid Log Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <Tabs defaultValue="summary">
              <TabsList>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="request">Request</TabsTrigger>
                <TabsTrigger value="response">Response</TabsTrigger>
              </TabsList>
              <TabsContent value="summary">
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Log ID</p>
                      <p className="font-mono text-sm">{selectedLog.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Timestamp</p>
                      <p className="font-medium">{new Date(selectedLog.timestamp).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">SSP</p>
                      <p className="font-medium">{selectedLog.ssp_name || selectedLog.ssp_id || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">DSP</p>
                      <p className="font-medium">{selectedLog.dsp_name || selectedLog.dsp_id || "N/A"}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <div className="mt-1">{getStatusBadge(selectedLog.status)}</div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ad Format</p>
                      <p className="font-medium">{selectedLog.ad_format || "Banner"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bid Floor</p>
                      <p className="font-medium font-mono">
                        ${(selectedLog.bid_floor ?? selectedLog.request_data?.imp?.[0]?.bidfloor ?? 0).toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bid Price</p>
                      <p className="font-medium font-mono text-green-600">
                        {selectedLog.bid_price ? `$${selectedLog.bid_price.toFixed(4)}` : "No bid"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Latency</p>
                      <p className="font-medium">{selectedLog.latency_ms ?? 0}ms</p>
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="request">
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs font-mono max-h-96">
                  {JSON.stringify(selectedLog.request_data || selectedLog.request, null, 2) || "No request data"}
                </pre>
              </TabsContent>
              <TabsContent value="response">
                <pre className="bg-muted p-4 rounded-lg overflow-auto text-xs font-mono max-h-96">
                  {selectedLog.response_data
                    ? JSON.stringify(selectedLog.response_data, null, 2)
                    : "No bid response"}
                </pre>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
